import { useEffect, useRef, useState } from "react";

import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { useClientSearch } from "~/pages/payments/hooks/useClientSearch";
import { ClientSuggestion } from "~/pages/payments/hooks/useClientSuggestions";

interface ClientSearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  defaultSuggestions?: ClientSuggestion[];
}

export default function ClientSearchSelect({
  value,
  onChange,
  defaultSuggestions = [],
}: ClientSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { searchResults, loading, searchClients, resetSearch } =
    useClientSearch();
  const lastQueryRef = useRef<string>("");
  const initialRender = useRef(true);
  const selectionMadeRef = useRef(false);

  // Create a memoized version of allSuggestions that won't recreate on every render
  const [allSuggestions, setAllSuggestions] = useState<ClientSuggestion[]>([
    ...defaultSuggestions,
  ]);

  // Effect to update allSuggestions when defaultSuggestions change
  useEffect(() => {
    setAllSuggestions([...defaultSuggestions]);
  }, [defaultSuggestions]);

  // Effect to update allSuggestions when searchResults change
  useEffect(() => {
    // Skip if selection was just made
    if (selectionMadeRef.current) {
      selectionMadeRef.current = false;
      return;
    }

    if (searchResults.length > 0) {
      const combined = [...defaultSuggestions];
      searchResults.forEach((result) => {
        if (!combined.some((suggestion) => suggestion.id === result.id)) {
          combined.push(result);
        }
      });
      setAllSuggestions(combined);
      if (!initialRender.current && !selectionMadeRef.current) {
        setOpen(true);
      }
    }
    initialRender.current = false;
  }, [searchResults, defaultSuggestions]);

  // Handle popover open state change
  const handleOpenChange = (newOpen: boolean) => {
    // Prevent event propagation
    setOpen(newOpen);

    // When opening the popover, reset the search state
    if (newOpen) {
      setSearchQuery("");
      lastQueryRef.current = "";
      resetSearch();
      if (defaultSuggestions.length > 0) {
        setAllSuggestions([...defaultSuggestions]);
      }
    }
  };

  // Get the selected client name for display
  const selectedClient =
    allSuggestions.find((client) => client.id === value) ||
    defaultSuggestions.find((client) => client.id === value);

  // Handle search query changes
  useEffect(() => {
    if (selectionMadeRef.current) return;

    if (searchQuery.length >= 2 && searchQuery !== lastQueryRef.current) {
      const debounce = setTimeout(() => {
        lastQueryRef.current = searchQuery;
        searchClients(searchQuery);
      }, 300);

      return () => clearTimeout(debounce);
    } else if (searchQuery.length === 0 && lastQueryRef.current !== "") {
      lastQueryRef.current = "";
      setAllSuggestions([...defaultSuggestions]);
    }
  }, [searchQuery, searchClients, defaultSuggestions]);

  // Automatically open popover when user starts typing
  useEffect(() => {
    if (selectionMadeRef.current) return;

    if (searchQuery.length >= 2 && !open) {
      setOpen(true);
    }
  }, [searchQuery, open]);

  // Effect to handle popover state when it closes
  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => {
        setSearchQuery("");
        lastQueryRef.current = "";
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [open]);

  // Handle input focus
  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setOpen(true);
    }
  };

  // Handle item selection
  const handleSelect = (value: string) => {
    const id = value.split("|")[0];
    selectionMadeRef.current = true;
    onChange(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="relative z-10 h-auto min-h-[2.5rem] justify-between px-3 py-2"
          type="button"
        >
          <div className="flex flex-col items-start gap-0.5 text-left">
            <span className="w-full truncate">
              {value && selectedClient
                ? selectedClient.name
                : "Buscar cliente..."}
            </span>
            {value && selectedClient?.taxId && (
              <span className="w-full truncate text-xs text-muted-foreground">
                RUT: {selectedClient.taxId}
              </span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 flex-none shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] max-w-[calc(90vw-2rem)] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        alignOffset={0}
        style={{
          zIndex: 99999,
          position: "fixed",
          pointerEvents: "auto",
        }}
        avoidCollisions={true}
        collisionPadding={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command
          className="max-h-[300px] overflow-auto"
          style={{ pointerEvents: "auto" }}
        >
          <CommandInput
            placeholder="Buscar por nombre o RUT..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            onFocus={handleInputFocus}
            autoFocus
            className="border-0"
            style={{ pointerEvents: "auto" }}
          />
          <CommandList>
            <CommandEmpty className="px-4 py-3">
              {loading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Buscando clientes...</span>
                </div>
              ) : searchQuery.length >= 2 ? (
                "No se encontraron clientes"
              ) : (
                "Escribe al menos 2 caracteres para buscar"
              )}
            </CommandEmpty>
            <CommandGroup>
              {allSuggestions.map((client) => (
                <CommandItem
                  key={client.id}
                  value={`${client.id}|${client.taxId || ""}|${client.name}`}
                  onSelect={handleSelect}
                  className="flex cursor-pointer items-start px-4 py-2"
                  style={{ pointerEvents: "auto" }}
                >
                  <Check
                    className={cn(
                      "mr-2 mt-1 h-4 w-4 flex-none",
                      value === client.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col overflow-hidden">
                    <span className="truncate font-medium">{client.name}</span>
                    {client.taxId && (
                      <span className="truncate text-xs text-muted-foreground">
                        RUT: {client.taxId}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
